<template>
  <card class="document-card margin-bottom-s" :custom-menu="isUploadedDocument">
    <div class="card-content flex-row align-center">
      <!-- Icon + Title on the right (flex-1 to take available space) -->
      <div class="document-left flex-row align-center"
           :class="{ 'flex-1': document.state === 'REQUIRED' || document.state === 'FAILED_VERIFICATION' }">
        <icon
          :icon-name="documentIcon"
          :size="'24px'"
          :color="Colors.riseupBlack"
          class="document-icon"
        />
        <div class="document-info flex-column margin-right-s">
          <div class="ri-body black-text">{{ documentTitle }}</div>
          <div v-if="document.budgetDate" class="ri-small-body gray-text">
            {{ formatBudgetDate(document.budgetDate) }}
          </div>
        </div>
      </div>

      <!-- Actions on the left -->
      <div class="document-actions flex-row align-center margin-right-auto">
        <!-- Upload button for REQUIRED or FAILED -->
        <template v-if="document.state === 'REQUIRED' || document.state === 'FAILED_VERIFICATION'">
          <riseup-button
            class="icon-only-btn"
            title=""
            color="green"
            variant="tertiary"
            size="small"
            :icon-props="addIconProps"
            @click="$emit('upload', document)"
          />
        </template>

        <!-- Status icon for uploaded documents -->
        <template v-else>
          <icon
            :icon-name="statusIcon"
            :size="'20px'"
            :color="statusColor"
            class="margin-left-s"
          />
        </template>
      </div>
    </div>

    <!-- Menu items for uploaded documents -->
    <template v-if="isUploadedDocument" v-slot:menuItems>
      <bottom-sheet-overflow-button
        :bottom-sheet-component="menuComponent"
        :component-props="menuComponentProps"
      />
    </template>
  </card>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import BottomSheetOverflowButton from '@/base-components/BottomSheetOverflowButton';
import DocumentMenu from './DocumentMenu.vue';

const DOCUMENT_TYPE_LABELS = {
  PAYCHECK: 'תלוש משכורת',
  TAX_RETURN: 'דוח מס',
  BANK_STATEMENT: 'אישור מס על הלוואות משכנתא',
  PROPERTY_APPRAISAL: 'שומה של הנכס',
  PURCHASE_AGREEMENT: 'הסכם רכישה',
  ID_CARD: 'תעודת זהות',
  OTHER: 'אחר',
};

const DOCUMENT_TYPE_ICONS = {
  PAYCHECK: 'receipt_long',
  TAX_RETURN: 'description',
  BANK_STATEMENT: 'account_balance',
  PROPERTY_APPRAISAL: 'home',
  PURCHASE_AGREEMENT: 'gavel',
  ID_CARD: 'badge',
  OTHER: 'insert_drive_file',
};

export default {
  name: 'DocumentCard',
  components: {
    Card: BaseUI.Card,
    RiseupButton: BaseUI.RiseupButton,
    Icon: BaseUI.Icon,
    BottomSheetOverflowButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      Colors: BaseUI.Colors,
      menuComponent: DocumentMenu,
    };
  },
  computed: {
    documentTitle() {
      return DOCUMENT_TYPE_LABELS[this.document.documentType] || this.document.documentType;
    },
    documentIcon() {
      return DOCUMENT_TYPE_ICONS[this.document.documentType] || DOCUMENT_TYPE_ICONS.OTHER;
    },
    addIconProps() {
      return {
        iconName: 'add',
        fill: false,
        weight: 'bold',
        size: '20px',
        color: BaseUI.Colors.riseupGreen,
      };
    },
    isUploadedDocument() {
      return this.document.state === 'UPLOADED' || this.document.state === 'VERIFIED';
    },
    statusIcon() {
      if (this.document.state === 'VERIFIED') { return 'check_circle'; }
      if (this.document.state === 'UPLOADED') { return 'schedule'; }
      return 'cancel';
    },
    statusColor() {
      if (this.document.state === 'VERIFIED') { return BaseUI.Colors.riseupGreen; }
      if (this.document.state === 'UPLOADED') { return BaseUI.Colors.riseupOrange; }
      return BaseUI.Colors.riseupRed;
    },
    verificationIcon() {
      return this.document.state === 'VERIFIED' ? 'check_circle' : 'cancel';
    },
    verificationColor() {
      return this.document.state === 'VERIFIED' ? BaseUI.Colors.riseupGreen : BaseUI.Colors.riseupRed;
    },
    verificationText() {
      if (this.document.state === 'VERIFIED') { return 'מאומת'; }
      if (this.document.state === 'UPLOADED') { return 'בבדיקה'; }
      return 'נדחה';
    },
    menuComponentProps() {
      return {
        menuItems: [
          {
            text: 'להחליף קובץ',
            iconProps: {
              iconName: 'upload',
              fill: true,
            },
            onClick: this.handleChangeFile,
          },
          {
            text: 'להוריד',
            iconProps: {
              iconName: 'download',
              fill: true,
            },
            onClick: this.handleDownload,
          },
        ],
        uploadDate: this.formatDate(this.document.updatedAt || this.document.createdAt),
        verificationIcon: this.verificationIcon,
        verificationColor: this.verificationColor,
        verificationText: this.verificationText,
        verificationNotes: this.document.verificationNotes || '',
      };
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) { return ''; }
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', { year: 'numeric', month: 'numeric', day: 'numeric' });
    },
    formatBudgetDate(dateString) {
      if (!dateString) { return ''; }
      return dateString;
    },
    handleChangeFile() {
      this.$emit('upload', this.document);
    },
    handleDownload() {
      this.$emit('download', this.document);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.document-card {
  background-color: $riseup_white;
  border-radius: 12px;
  padding: $ri-spacing-s $ri-spacing-m;

  .card-content {
    width: 100%;
  }

  .document-left {
    text-align: right;
  }

  .document-icon {
    margin-left: $ri-spacing-s;
  }

  .document-info {
    flex: 1;
    text-align: right;
  }

  .gray-text {
    color: $riseup_gray_0;
  }

  .icon-only-btn {
    ::v-deep .button-content {
      padding: 0;
      min-width: 32px;
      min-height: 32px;
    }
  }
}
</style>
